import React, { useState, useEffect } from 'react';
import { Typography, Table, Button, message } from 'antd';
import DashboardLayout from '../components/DashboardLayout';
import { useReadContract } from 'wagmi';
import { useAccount } from 'wagmi';
import { CONTRACT_ADDRESS, CONTRACT_ABI } from '../config/contractConfig';
import { BASE_URL } from "../utils/URL_Config";

const { Title } = Typography;

type ProjectData = {
  key: string;
  projectId: string;
  projectUrl: string;
  invitationCode: string;
  successfulInvitations: number;
};

const Promotions: React.FC = () => {
  const [dataSource, setDataSource] = useState<ProjectData[]>([]);

  const { address: userAddress } = useAccount();

  // Fetch projects created by the current user
  const { data: userProjects, isLoading: loadingProjects } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: CONTRACT_ABI,
    functionName: 'getAddressProjects',
    args: [userAddress, 0, 100],
  });

  // Fetch invitation codes
  const { data: inviteCodes, isLoading: loadingInviteCodes } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: CONTRACT_ABI,
    functionName: 'getAddressInviteCodes',
    args: [userAddress, 0, 15],
  });

  useEffect(() => {
    if (userProjects && inviteCodes && Array.isArray(userProjects) && Array.isArray(inviteCodes)) {
      // Filter projects that have a valid invite code
      const filteredProjects = userProjects.filter((project: any) =>
        inviteCodes.some((code: any) => code.projectId === project.projectId)
      );

      // Format the data to match the table structure
      const formattedData: ProjectData[] = filteredProjects.map((project: any, index: number) => {
        const inviteCodeData = inviteCodes.find(
          (code: any) => code.projectId === project.projectId
        );

        return {
          key: index.toString(),
          projectId: project.projectId,
          projectUrl: project.url,
          invitationCode: inviteCodeData ? inviteCodeData.code : 'N/A',
          successfulInvitations: project.inviteCount || 0,
        };
      });

      setDataSource(formattedData);
    }
  }, [userProjects, inviteCodes]);

  const handleCopyLink = (projectId: string, invitationCode: string) => {
    const inviteLink = `${BASE_URL}/accept-invite?projectid=${projectId}&code=${invitationCode}`;
    navigator.clipboard.writeText(inviteLink)
      .then(() => {
        message.success('Invitation link copied to clipboard!');
      })
      .catch(() => {
        message.error('Failed to copy link. Please try again.');
      });
  };

  const columns = [
    {
      title: 'Project URL',
      dataIndex: 'projectUrl',
      key: 'projectUrl',
    },
    {
      title: 'Invitation Code',
      dataIndex: 'invitationCode',
      key: 'invitationCode',
    },
    {
      title: 'Successful Invitations',
      dataIndex: 'successfulInvitations',
      key: 'successfulInvitations',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text: string, record: ProjectData) => (
        <Button
          type="primary"
          onClick={() => handleCopyLink(record.projectId, record.invitationCode)}
          disabled={record.invitationCode === 'N/A'}
        >
          Generate Invite Link
        </Button>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <div style={{ padding: '1rem' }}>
        <Title level={3}>Promotions</Title>
        <p>Manage your promotions and track the performance of your projects here.</p>
        <Table
          dataSource={dataSource}
          columns={columns}
          bordered
          loading={loadingProjects || loadingInviteCodes}
          pagination={{ pageSize: 5 }}
        />
      </div>
    </DashboardLayout>
  );
};

export default Promotions;
